/**
 * React Query hooks for GDAP Relationship API (COMP-004).
 * IP-004: admin-only GDAP management endpoints.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "@/providers/api-provider";
import type { GDAPRelationship, PaginatedResponse } from "@/types";

export interface GdapRelationshipDetail {
  id: string;
  customerTenantName: string;
  customerTenantId: string;
  status: string;
  accessType: string;
  serviceTier: string;
  durationDays: number;
  createdDateTime: string | null;
  endDateTime: string | null;
  lastReconciliation: string | null;
  approvalUrl?: string;
  expiryDaysRemaining: number | null;
  accessAssignments: {
    id: string;
    securityGroupId: string;
    securityGroupName: string;
    accessLevel: string;
    roles: string[];
    status: string;
  }[];
  driftStatus: {
    unresolvedCount: number;
    drifts: {
      id: string;
      driftType: string;
      description: string;
      resolution: string | null;
      detectedAt: string;
    }[];
  };
}

interface GdapListParams {
  page?: number;
  pageSize?: number;
  status?: string;
  search?: string;
  customerTenantId?: string;
}

interface CreateRelationshipRequest {
  customerTenantId: string;
  durationDays: number;
}

interface GdapDurationSetting {
  defaultDurationDays: number;
}

interface ReconcileResult {
  reconciled: number;
  drifts: number;
}

export function useGdapRelationships(params: GdapListParams = {}) {
  const api = useApiClient();
  const { page = 1, pageSize = 25, status, search, customerTenantId } = params;

  const queryParams = new URLSearchParams();
  queryParams.set("page", String(page));
  queryParams.set("pageSize", String(pageSize));
  if (status) queryParams.set("status", status);
  if (search) queryParams.set("search", search);
  if (customerTenantId) queryParams.set("customerTenantId", customerTenantId);

  return useQuery({
    queryKey: ["gdap", "relationships", { page, pageSize, status, search, customerTenantId }],
    queryFn: () =>
      api.get<PaginatedResponse<GDAPRelationship>>(
        `/api/admin/gdap/relationships?${queryParams.toString()}`,
      ),
    staleTime: 60_000,
  });
}

export function useGdapRelationshipDetail(relationshipId: string | undefined) {
  const api = useApiClient();

  return useQuery({
    queryKey: ["gdap", "relationships", relationshipId],
    queryFn: () =>
      api.get<GdapRelationshipDetail>(
        `/api/admin/gdap/relationships/${relationshipId}`,
      ),
    enabled: !!relationshipId,
    staleTime: 30_000,
  });
}

export interface CreateRelationshipResponse {
  id: string;
  customerTenantName: string;
  status: string;
  approvalUrl: string;
  createdDateTime: string;
}

export function useCreateRelationship() {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: CreateRelationshipRequest) =>
      api.post<CreateRelationshipResponse>(
        "/api/admin/gdap/relationships",
        request,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["gdap"] });
    },
  });
}

export function useTerminateRelationship() {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (relationshipId: string) =>
      api.del<void>(`/api/admin/gdap/relationships/${relationshipId}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["gdap"] });
    },
  });
}

export function useReconcileRelationships() {
  const api = useApiClient();

  return useMutation({
    mutationFn: () =>
      api.post<ReconcileResult>("/api/admin/gdap/reconcile", {}),
  });
}

export function useGdapDuration() {
  const api = useApiClient();

  return useQuery({
    queryKey: ["gdap", "duration"],
    queryFn: () =>
      api.get<GdapDurationSetting>("/api/admin/settings/gdap-duration"),
    staleTime: 300_000,
  });
}

export function useUpdateGdapDuration() {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (setting: GdapDurationSetting) =>
      api.put<GdapDurationSetting>(
        "/api/admin/settings/gdap-duration",
        setting,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["gdap", "duration"] });
    },
  });
}
